import React, { useEffect, useMemo, useState } from 'react';
import { createSale, getAppointments, getPatients, getProducts, getSales } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../utils/apiError';
import { Plus, ReceiptText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const emptyItem = { product_id: '', quantity: 1 };

export default function Sales() {
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [patients, setPatients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [products, setProducts] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    patient_id: '',
    appointment_id: '',
    sale_date: new Date().toISOString().slice(0, 16),
    payment_method: '',
    notes: '',
    items: [emptyItem],
  });

  const fetchData = async () => {
    try {
      const [salesRes, patientsRes, productsRes, appointmentsRes] = await Promise.all([
        getSales(),
        getPatients(),
        getProducts(false),
        getAppointments(),
      ]);
      setSales(Array.isArray(salesRes.data?.data) ? salesRes.data.data : []);
      setPatients(Array.isArray(patientsRes.data?.data) ? patientsRes.data.data : []);
      setProducts(Array.isArray(productsRes.data?.data) ? productsRes.data.data : []);
      setAppointments(Array.isArray(appointmentsRes.data?.data) ? appointmentsRes.data.data : []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al cargar ventas'));
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [String(p.id), p])), [products]);

  const calculatedSubtotal = useMemo(() => {
    return formData.items.reduce((sum, line) => {
      const product = productMap[String(line.product_id)];
      if (!product) return sum;
      const qty = Number(line.quantity || 0);
      return sum + Number(product.sale_price) * qty;
    }, 0);
  }, [formData.items, productMap]);

  const openNewSale = () => {
    setFormData({
      patient_id: '',
      appointment_id: '',
      sale_date: new Date().toISOString().slice(0, 16),
      payment_method: '',
      notes: '',
      items: [emptyItem],
    });
    setDrawerOpen(true);
  };

  const updateItem = (index, patch) => {
    setFormData((prev) => {
      const next = [...prev.items];
      next[index] = { ...next[index], ...patch };
      return { ...prev, items: next };
    });
  };

  const addItem = () => setFormData((prev) => ({ ...prev, items: [...prev.items, emptyItem] }));
  const removeItem = (index) => setFormData((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));

  const submitSale = async (e) => {
    e.preventDefault();
    const cleanItems = formData.items
      .filter((line) => line.product_id && Number(line.quantity) > 0)
      .map((line) => ({ product_id: Number(line.product_id), quantity: Number(line.quantity) }));

    if (cleanItems.length === 0) {
      toast.error('Agrega al menos un producto');
      return;
    }

    setIsLoading(true);
    try {
      await createSale({
        patient_id: formData.patient_id ? Number(formData.patient_id) : null,
        appointment_id: formData.appointment_id ? Number(formData.appointment_id) : null,
        sale_date: formData.sale_date ? new Date(formData.sale_date).toISOString() : null,
        payment_method: formData.payment_method || null,
        notes: formData.notes || null,
        items: cleanItems,
      });
      toast.success('Venta registrada');
      setDrawerOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al registrar venta'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#2B2D42] font-['Manrope']">Ventas</h1>
          <p className="text-[#5D5C61] mt-1">Registro de ventas de productos</p>
        </div>
        <Button className="bg-[#335C67] hover:bg-[#244048] text-white rounded-full" onClick={openNewSale}>
          <Plus className="w-4 h-4 mr-2" />
          Nueva venta
        </Button>
      </div>

      <div className="grid gap-4">
        {sales.map((sale) => (
          <Card key={sale.id} className="bg-white border-slate-100 cursor-pointer hover:border-[#335C67]/30" onClick={() => navigate(`/ventas/${sale.id}`)}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">{sale.folio}</CardTitle>
                  <CardDescription>
                    {new Date(sale.sale_date).toLocaleString('es-MX')} · {sale.patient?.full_name || 'Sin cliente'}
                  </CardDescription>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-[#2B2D42]">${Number(sale.total || 0).toFixed(2)}</p>
                  <p className="text-xs text-[#8D99AE]">{sale.items?.length || 0} productos</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-xs text-[#8D99AE]">{sale.appointment_id ? `Asociada a cita #${sale.appointment_id}` : 'Venta independiente'}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-2xl font-['Manrope']">Nueva venta</SheetTitle>
            <SheetDescription>Registra productos vendidos y descuenta inventario automaticamente.</SheetDescription>
          </SheetHeader>

          <form onSubmit={submitSale} className="mt-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Cliente (opcional)</Label>
                <Select value={formData.patient_id || 'none'} onValueChange={(v) => setFormData({ ...formData, patient_id: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin cliente</SelectItem>
                    {patients.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Cita relacionada (opcional)</Label>
                <Select value={formData.appointment_id || 'none'} onValueChange={(v) => setFormData({ ...formData, appointment_id: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin cita</SelectItem>
                    {appointments.slice(0, 50).map((a) => (
                      <SelectItem key={a.id} value={String(a.id)}>
                        #{a.id} · {a.date} {a.start_time} · {a.patient?.full_name || 'Sin cliente'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Fecha de venta</Label>
              <Input type="datetime-local" value={formData.sale_date} onChange={(e) => setFormData({ ...formData, sale_date: e.target.value })} />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Productos</Label>
                <Button type="button" variant="outline" onClick={addItem}>Agregar linea</Button>
              </div>
              {formData.items.map((line, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-7 space-y-1">
                    <Label className="text-xs">Producto</Label>
                    <Select value={line.product_id || ''} onValueChange={(v) => updateItem(index, { product_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Selecciona producto" /></SelectTrigger>
                      <SelectContent>
                        {products.map((p) => (
                          <SelectItem key={p.id} value={String(p.id)}>
                            {p.name} · ${Number(p.sale_price).toFixed(2)} · stock {p.stock}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3 space-y-1">
                    <Label className="text-xs">Cantidad</Label>
                    <Input type="number" min="1" value={line.quantity} onChange={(e) => updateItem(index, { quantity: e.target.value })} />
                  </div>
                  <div className="col-span-2">
                    <Button type="button" variant="ghost" className="w-full text-[#9E2A2B]" onClick={() => removeItem(index)} disabled={formData.items.length === 1}>Quitar</Button>
                  </div>
                  <div className="col-span-12 text-xs text-[#8D99AE]">
                    Subtotal: ${(() => {
                      const p = productMap[String(line.product_id)];
                      const qty = Number(line.quantity || 0);
                      return p ? (Number(p.sale_price) * qty).toFixed(2) : '0.00';
                    })()}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Metodo de pago</Label>
                <Input value={formData.payment_method} onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })} placeholder="Efectivo, tarjeta, transferencia..." />
              </div>
              <div className="space-y-2">
                <Label>Total</Label>
                <div className="h-10 rounded-md border px-3 flex items-center font-semibold">
                  ${calculatedSubtotal.toFixed(2)}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} />
            </div>

            <Button type="submit" className="w-full bg-[#335C67] hover:bg-[#244048] text-white rounded-full" disabled={isLoading}>
              {isLoading ? 'Guardando...' : 'Guardar venta'}
            </Button>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}

